// Mirror of the API manifest's role → capability bundles.
// Source of truth: apps/api/src/auth/manifest.ts (pushed to Keystone at boot).
// Keep these in sync when manifest changes.
export const ROLE_CAPABILITIES: Record<string, Array<string>> = {
	'blueprint:super_admin': [
		'read:blueprint', 'create:blueprint', 'update:blueprint', 'delete:blueprint',
		'read:price-book',
		'read:customer', 'list:customer',
		'read:project', 'read:metadata',
		'read:admin', 'write:admin',
		'list:users', 'read:users', 'write:users', 'delete:users',
		'export-pdf', 'rendering', 'tax', 'crm:write',
		'view:blueprint-document', 'read:products', 'write:products',
		'write:price-book', 'write:price-book-targeting', 'write:price-book-schedule'
	],
	'blueprint:admin': [
		'read:blueprint', 'create:blueprint', 'update:blueprint',
		'read:price-book',
		'read:customer', 'list:customer',
		'read:project', 'read:metadata',
		'read:admin', 'write:admin',
		'list:users', 'read:users', 'write:users',
		'export-pdf', 'rendering', 'tax', 'crm:write',
		'view:blueprint-document', 'read:products', 'write:products',
		'write:price-book', 'write:price-book-targeting', 'write:price-book-schedule'
	],
	'blueprint:srblueprint': [
		'read:blueprint', 'create:blueprint', 'update:blueprint',
		'read:price-book',
		'read:customer', 'list:customer',
		'read:project', 'read:metadata',
		'read:admin', 'write:admin',
		'export-pdf', 'rendering', 'tax', 'crm:write',
		'view:blueprint-document', 'read:products', 'write:products'
	],
	'blueprint:blueprint': [
		'read:blueprint', 'create:blueprint', 'update:blueprint',
		'read:price-book',
		'read:customer', 'list:customer',
		'read:project', 'read:metadata',
		'export-pdf', 'rendering', 'tax', 'crm:write',
		'view:blueprint-document'
	],
	'blueprint:srdesigner': [
		'read:blueprint', 'create:blueprint', 'update:blueprint',
		'read:price-book',
		'read:customer',
		'read:project', 'read:metadata',
		'read:admin', 'write:admin',
		'export-pdf', 'rendering', 'tax', 'crm:write',
		'view:blueprint-document'
	],
	'blueprint:designer': [
		'read:blueprint', 'create:blueprint', 'update:blueprint',
		'read:price-book',
		'read:customer',
		'read:project', 'read:metadata',
		'export-pdf', 'rendering', 'tax', 'crm:write'
	],
	'blueprint:user': [
		'read:blueprint'
	]
};

// Union of every capability declared by any role — used as the effective set
// for super_admin and admin so they implicitly inherit anything added later.
const ALL_CAPABILITIES: Array<string> = (() => {
	const set = new Set<string>();
	for (const caps of Object.values(ROLE_CAPABILITIES)) {
		for (const c of caps) set.add(c);
	}
	return Array.from(set);
})();

const SUPERSET_ROLES = new Set<string>(['blueprint:super_admin', 'blueprint:admin']);

export const expandCapabilities = (appRoles: Array<string>): Array<string> => {
	if (appRoles.some((r) => SUPERSET_ROLES.has(r))) return [...ALL_CAPABILITIES];
	const set = new Set<string>();
	for (const role of appRoles) {
		const caps = ROLE_CAPABILITIES[role];
		if (!caps) continue;
		for (const c of caps) set.add(c);
	}
	return Array.from(set);
};

// Priority order matches the key order of ROLE_CAPABILITIES (highest first).
const ROLE_PRIORITY = Object.keys(ROLE_CAPABILITIES);

export const getHighestRole = (appRoles: Array<string>): string | null => {
	for (const candidate of ROLE_PRIORITY) {
		if (appRoles.includes(candidate)) return candidate;
	}
	return null;
};
